import { FORM_ISSUE_CODES, type FormIssueCode } from '@btsis/core';
import { z } from 'zod';

export const errorDetailSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  requestId: z.string().optional(),
});

export type ErrorDetail = z.infer<typeof errorDetailSchema>;

export const commonErrors = {
  UNAUTHENTICATED: { status: 401, data: errorDetailSchema },
  INVALID_CREDENTIALS: { status: 401, data: errorDetailSchema },
  ACCOUNT_LOCKED: { status: 423, data: errorDetailSchema },
  TOO_MANY_REQUESTS: { status: 429, data: errorDetailSchema },
  CSRF_FAILED: { status: 403, data: errorDetailSchema },
  TWO_FACTOR_REQUIRED: { status: 403, data: errorDetailSchema },
  REVEAL_REQUIRED: { status: 422, data: errorDetailSchema },
  PERMISSION_DENIED: { status: 403, data: errorDetailSchema },
  OUT_OF_SCOPE: { status: 403, data: errorDetailSchema },
  NOT_FOUND: { status: 404, data: errorDetailSchema },
  CONFLICT: { status: 409, data: errorDetailSchema },
  VALIDATION_FAILED: { status: 422, data: errorDetailSchema },
  PRICING_VIOLATION: { status: 422, data: errorDetailSchema },
  DONATION_DATE_OUTSIDE_PERIOD: { status: 422, data: errorDetailSchema },
  DONATION_DATE_IN_FUTURE: { status: 422, data: errorDetailSchema },
  DONATION_BACKDATE_NOT_PERMITTED: { status: 403, data: errorDetailSchema },
} as const;

/**
 * The form engine's codes, read off the Zod issue rather than an exception. The
 * envelope's `code` is the first issue's, and `details.issues` carries every
 * `{ code, path }` so a form can put each message on its own field.
 */
export const formErrors = Object.fromEntries(
  FORM_ISSUE_CODES.map((code) => [code, { status: 422, data: errorDetailSchema }]),
) as Record<FormIssueCode, { status: 422; data: typeof errorDetailSchema }>;
